/**
 * Default reference lists. Seeded into reference_lists / reference_values on a
 * fresh install and then edited by an administrator (deactivate, never delete).
 * The code references values by stable key, never by label or position.
 */
export interface ReferenceListDef {
  key: string;
  name: string;
  description: string;
  system?: boolean;
  values: Array<{ key: string; label: string; labelHe?: string; colour?: string; metadata?: Record<string, unknown> }>;
}

const simple = (items: Array<[string, string] | [string, string, string]>) =>
  items.map(([key, label, colour]) => (colour ? { key, label, colour } : { key, label }));

export const DEFAULT_REFERENCE_LISTS: ReferenceListDef[] = [
  {
    key: "inspection_frequency",
    name: "Inspection frequencies",
    description: "How often a facility or plant is visited. All nine values in live use, plus special.",
    values: simple([
      ["weekly", "Weekly"],
      ["twice_a_week", "Twice a week"],
      ["three_times_a_week", "3 times a week"],
      ["bi_weekly", "Bi-weekly"],
      ["once_in_3_weeks", "Once in 3 weeks"],
      ["monthly", "Monthly"],
      ["quarterly", "Quarterly"],
      ["once_in_4_months", "Once in 4 months"],
      ["yearly", "Yearly"],
      ["special", "Special"],
    ]),
  },
  {
    key: "agency",
    name: "Certifying agencies",
    description: "Kosher agencies whose certificates are accepted on ingredients.",
    // `aliases` are the legacy portal's spellings of the same agency (its register, /admin/car/manage): the register
    // import and the master-ingredient import resolve them to this key instead of adding a second value
    values: [
      { key: "crc", label: "CRC", metadata: { aliases: ["Central Rabbinical Congress (Hisachdus HaRabanim) CRC", "Central Rabbinical Congress of the U.S.A. and Canada"] } },
      { key: "ou", label: "OU", metadata: { aliases: ["Orthodox Union ( OU )", "Orthodox Union (OU)", "Orthodox Union"] } },
      { key: "ou_fish", label: "OU Fish", metadata: { aliases: [] } },
      { key: "ok", label: "OK", metadata: { aliases: ["OK Kosher Certification", "OK Kosher"] } },
      { key: "star_k", label: "Star-K", metadata: { aliases: ["Star-K Kosher Certification", "Star K"] } },
      { key: "kof_k", label: "Kof-K", metadata: { aliases: ["Kof-K Kosher Supervision", "Kof K"] } },
      { key: "ckc", label: "cRc", metadata: { aliases: ["Chicago Rabbinical Council (cRc)", "Chicago Rabbinical Council"] } },
      { key: "mk", label: "MK", metadata: { aliases: ["MK - Jewish Community Council of Montreal", "Jewish Community Council of Montreal (MK)"] } },
      { key: "badatz", label: "Badatz", metadata: { aliases: ["Bedatz - The Beth Din Zedek of the Eida HaCharedis of Jerusalem (Orthodox Council of Jerusalem)", "Badatz Eida HaCharedis"] } },
      { key: "other", label: "Other", metadata: { aliases: [] } },
    ],
  },
  {
    key: "designation",
    name: "Kosher designations",
    description: "The designation of an ingredient or product.",
    values: simple([
      ["pareve", "Pareve"],
      ["dairy", "Dairy"],
      ["meat", "Meat"],
      ["fish", "Fish"],
      ["dairy_equipment", "Dairy equipment"],
    ]),
  },
  {
    key: "restriction",
    name: "Restriction types",
    description: "Restriction on a company or product.",
    values: simple([
      ["pareve", "Pareve"],
      ["dairy", "Dairy"],
      ["meat", "Meat"],
      ["glatt", "Glatt"],
      ["symbol", "Symbol"],
      ["cholov_yisroel", "Cholov Yisroel"],
      ["pas_yisroel", "Pas Yisroel"],
      ["none", "None"],
    ]),
  },
  {
    key: "ingredient_group",
    name: "Ingredient groups",
    description: "Ingredient category. Group 1 is the least sensitive (salt, sugar).",
    values: simple([
      ["1", "Group 1"],
      ["2", "Group 2"],
      ["3", "Group 3"],
      ["4", "Group 4"],
      ["5", "Group 5"],
    ]),
  },
  {
    key: "doc_type",
    name: "Document types",
    description: "Kinds of document attached to a record.",
    values: simple([
      ["certificate", "Kosher certificate"],
      ["spec_sheet", "Spec sheet"],
      ["label", "Label artwork"],
      ["flow_chart", "Flow chart"],
      ["protocol", "Kosher protocol"],
      ["contract", "Contract"],
      ["inspection_report", "Inspection report"],
      ["loc", "Letter of certification"],
      ["invoice", "Invoice"],
      ["photo", "Photograph"],
      ["agreement", "Agreement"],
      ["other", "Other"],
    ]),
  },
  {
    key: "charge_kind",
    name: "Charge kinds",
    description: "Kinds of charge raised on a company.",
    values: simple([
      ["monthly", "Monthly fee"],
      ["annual", "Annual fee"],
      ["admin", "Administration fee"],
      ["one_time", "One-time charge"],
      ["production", "Production visit"],
      ["event", "Event supervision"],
      ["seal", "Seals"],
      ["seals", "Seals (per seal)"],
      ["stam", "STaM certification (seal included)"],
      ["wine", "Wine supervision"],
      ["certification", "Certification"],
      ["private_label", "Private label"],
      ["additional_facility", "Additional facility"],
      ["initial_inspection", "Initial inspection"],
      ["special_production", "Special production"],
      ["authorization", "Authorization"],
      ["kosherization", "Kosherization"],
      ["inspection", "Inspection visit"],
      ["admin_fee", "Administration fee"],
      ["other", "Other"],
    ]),
  },
  {
    key: "work_kind",
    name: "Work kinds (invoice appointments)",
    description: "What a mashgiach is being asked to invoice. Only production work waits for the customer to pay.",
    values: simple([
      ["production", "Production"],
      ["food_service", "Food service"],
      ["event", "Event"],
      ["other", "Other"],
    ]),
  },
  {
    key: "issue_severity",
    name: "Issue severities",
    description: "Severity of an inspection issue or complaint.",
    values: simple([
      ["minor", "Minor", "attention"],
      ["major", "Major", "critical"],
      ["critical", "Critical / hold production", "critical"],
    ]),
  },
  {
    key: "issue_resolution",
    name: "Resolution statuses",
    description: "How an issue found on an inspection stands.",
    values: simple([
      ["resolved_on_site", "Resolved on site"],
      ["open_follow_up", "Open — follow-up needed"],
      ["escalated_to_rc", "Escalated to RC"],
    ]),
  },
  {
    key: "contract_type",
    name: "Contract types",
    description: "Informational contract type on a company.",
    values: simple([
      ["standard", "Standard"],
      ["food_service", "Food service"],
      ["private_label", "Private label"],
      ["event", "Event"],
    ]),
  },
  {
    key: "listing_category",
    name: "Listing categories",
    description: "Directory category of a public listing (ClickUp: Listing Type on the company).",
    values: simple([
      ["appetizing", "Appetizing"],
      ["bakery_store", "Bakery store"],
      ["butcher", "Butcher"],
      ["caterer", "Caterer"],
      ["dairy_restaurant", "Restaurant — dairy"],
      ["meat_restaurant", "Restaurant — meat"],
      ["grocery", "Grocery"],
      ["manufacturer", "Manufacturer"],
      ["pizza", "Pizza"],
      ["take_out", "Take-out"],
      ["tashmishei_kedusha", "Tashmishei Kedusha"],
      ["other", "Other"],
    ]),
  },
  {
    key: "loc_phrase_public",
    name: "Certificate wording — public",
    description: "Controlled vocabulary printed on public certificates.",
    values: simple([
      ["pareve", "Pareve"],
      ["dairy", "Dairy"],
      ["dairy_cholov_yisroel", "Dairy — Cholov Yisroel"],
      ["meat_glatt", "Meat — Glatt Kosher"],
      ["pas_yisroel", "Pas Yisroel"],
      ["kosher_for_passover", "Kosher for Passover"],
      ["when_bearing_symbol", "When bearing the CRC symbol"],
      ["when_bearing_on_label", "When bearing on label"],
      ["fish", "Fish"],
      ["mevushal", "Mevushal"],
      ["not_mevushal", "Not Mevushal"],
      ["yoshon", "Yoshon"],
    ]),
  },
  {
    key: "seal_void_reason",
    name: "Seal void reasons",
    description: "Why a seal number was voided (from KosherSeal, in use today).",
    values: simple([
      ["damaged_torn", "Damaged / torn"],
      ["contaminated", "Contaminated"],
      ["misprinted", "Misprinted"],
      ["incorrect_product", "Incorrect product"],
      ["lost", "Lost"],
      ["other", "Other"],
    ]),
  },
  {
    key: "seal_storage_location",
    name: "Seal storage locations",
    description: "Where printed rolls are kept before assignment.",
    values: simple([
      ["office_safe", "Office safe"],
      ["lakewood_depot", "Lakewood depot"],
    ]),
  },
  {
    key: "issue_team",
    name: "Compliance teams",
    description: "The team a complaint or issue routes to, alongside its owner (FS 1 · FS 2 · Production today).",
    values: simple([
      ["fs_1", "FS 1"],
      ["fs_2", "FS 2"],
      ["production", "Production"],
    ]),
  },
  {
    key: "complaint_type",
    name: "Complaint types",
    description: "Kashrus complaint types (de-duplicated from the live list).",
    values: simple([
      ["unsupervised_production", "Unsupervised production"],
      ["working_shabbos", "Working Shabbos"],
      ["unapproved_ingredient", "Unapproved ingredient"],
      ["mislabeling", "Mislabeling"],
      ["symbol_misuse", "Symbol misuse"],
      ["mashgiach_absent", "Mashgiach absent"],
      ["cleanliness", "Cleanliness"],
      ["other", "Other"],
    ]),
  },
  {
    key: "location_status",
    name: "Location statuses",
    description: "Status of a facility or plant.",
    values: simple([
      ["active", "Active", "good"],
      ["pending", "Pending", "attention"],
      ["seasonal", "Seasonal", "neutral"],
      ["inactive", "Inactive", "neutral"],
    ]),
  },
  {
    key: "jewish_ownership",
    name: "Jewish ownership",
    description: "Whether the company is Jewish-owned (drives Passover sale of chometz).",
    values: simple([
      ["yes", "Yes"],
      ["no", "No"],
      ["unknown", "Unknown"],
    ]),
  },
  {
    key: "ingredient_project_type",
    name: "Ingredient project types",
    description: "The seven ways ingredient review is triggered.",
    values: simple([
      ["new_ingredient", "New ingredient"],
      ["new_product", "New product"],
      ["new_plant", "New plant"],
      ["old_plant_entry", "Old plant entry"],
      ["update_ingredient", "Update ingredient"],
      ["crc_services", "CRC services"],
      ["review_and_match", "Review and match"],
    ]),
  },
  {
    key: "credential_kind",
    name: "Practitioner credentials",
    description: "What a sofer or bodek holds that can lapse. A lapse warns at seal issue; it never blocks.",
    values: simple([
      ["kabbalah", "Kabbalah"],
      ["reshus_beis_din", "רשות בי\"ד"],
      ["certificate", "Certificate"],
      ["other", "Other"],
    ]),
  },
  {
    key: "person_relation",
    name: "Person references",
    description: "How one person relates to another — his rav, his STaM rav, who gave his kabbalah, who supervises him, who he sells through. Links, never text.",
    values: simple([
      ["rav", "His rav"],
      ["rav_stam", "His STaM rav"],
      ["rav_kabbalah", "Gave his kabbalah"],
      ["supervised_by", "Supervised by"],
      ["sells_through", "Sells through (merchant)"],
    ]),
  },
  {
    key: "equipment_kind",
    name: "Location equipment",
    description: "What a winery (or any location) registers so a report picks it rather than types it.",
    values: simple([
      ["tank", "Tank"],
      ["press", "Press"],
      ["crusher", "Crusher"],
      ["pasteurizer", "Pasteurizer"],
      ["bottling_line", "Bottling line"],
      ["other", "Other"],
    ]),
  },
  {
    key: "membership_cadence",
    name: "Membership cadences",
    description: "How often a membership plan charges. `months` in the metadata is the length of one period; a new cadence is a row.",
    values: [
      { key: "monthly", label: "Monthly", metadata: { months: 1 } },
      { key: "quarterly", label: "Quarterly", metadata: { months: 3 } },
      { key: "half_yearly", label: "Every six months", metadata: { months: 6 } },
      { key: "yearly", label: "Yearly", metadata: { months: 12 } },
    ],
  },
  {
    key: "member_status",
    name: "Member statuses",
    description: "Where a member stands. The platform moves a member to active on a paid charge, lapsed when a charge stays unpaid past the grace period, and cancelled when someone cancels; the labels and colours are yours.",
    values: simple([
      ["prospect", "Prospect", "neutral"],
      ["active", "Active", "success"],
      ["lapsed", "Lapsed", "warning"],
      ["cancelled", "Cancelled", "danger"],
    ]),
  },
  {
    key: "membership_subscription_status",
    name: "Subscription statuses",
    description: "A subscription is charged only while its status carries `charges: true` in the metadata. Past due keeps charging (retries); paused, cancelled and lapsed do not.",
    values: [
      { key: "active", label: "Active", colour: "success", metadata: { charges: true } },
      { key: "past_due", label: "Past due", colour: "warning", metadata: { charges: true } },
      { key: "paused", label: "Paused", colour: "neutral", metadata: { charges: false } },
      { key: "lapsed", label: "Lapsed", colour: "warning", metadata: { charges: false } },
      { key: "cancelled", label: "Cancelled", colour: "danger", metadata: { charges: false } },
    ],
  },
  {
    key: "member_question_category",
    name: "Member question categories",
    description: "What a member's question through the portal is about.",
    values: simple([
      ["kashrus", "Kashrus"],
      ["pesach", "Kashrus · Pesach"],
      ["halacha", "Halacha"],
      ["membership", "My membership"],
      ["other", "Other"],
    ]),
  },
];
